import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks';
import { useRadioStation, useUpdateRadioStation } from '@/hooks';
import { CreateRadioStationRequest } from '@/types/model';
import RadioStationForm from '@/components/forms/RadioStationForm';
import Button from '@/components/ui/Button.tsx';
import toast from 'react-hot-toast';

const EditRadioStation = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: radioStation, isLoading, isError, error } = useRadioStation(id!);
  const updateRadioStationMutation = useUpdateRadioStation();

  const handleSuccess = async (data: CreateRadioStationRequest) => {
    try {
      await updateRadioStationMutation.mutateAsync({ id: id!, data });
      toast.success(t.radioStations.messages.updated);
      navigate('/content/radio-stations');
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
      throw error; // Re-throw to let form handle the error
    }
  };

  const handleCancel = () => {
    navigate('/content/radio-stations');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !radioStation) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">
              {error instanceof Error ? error.message : t.errors.loadingError}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          onClick={handleCancel}
          variant="link"
        >
          ← Volver a la lista
        </Button>

        <RadioStationForm
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          initialData={radioStation}
          isEdit={true}
          isSubmitting={updateRadioStationMutation.isPending}
        />
      </div>
    </div>
  );
};

export default EditRadioStation;
